import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, RefreshCw } from 'lucide-react';
import { motion } from 'framer-motion';
import FeedList from './components/FeedList';
import { useFormFeeds } from './hooks/useFormFeeds';
import Skeleton from '../../components/Skeleton';

const FormFeedsPage: React.FC = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const { feeds, loading, totalPages, total, fetchFeeds, deleteFeed } = useFormFeeds(currentPage, 15);

  return (
    <div className="h-full flex flex-col">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between mb-6"
      >
        <div>
          <h1 className="text-2xl font-bold theme-text-primary">Form Feeds</h1>
          <p className="text-sm theme-text-tertiary mt-1">
            Manage form feeds and field mappings ({total} total)
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={fetchFeeds}
            className="px-4 py-2 theme-bg-tertiary hover:theme-bg-quaternary theme-text-primary rounded-lg font-medium transition-colors flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={() => navigate('/form-feeds/create')}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Create Feed
          </button>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="flex-1 theme-bg-primary rounded-lg shadow-sm border theme-border overflow-hidden flex flex-col"
      >
        {loading ? (
          <div className="flex-1 p-4 space-y-3">
            {/* Table header skeleton */}
            <div className="flex gap-4 pb-2 border-b theme-border">
              <Skeleton width={120} height={14} variant="rounded" />
              <Skeleton width={80} height={14} variant="rounded" />
              <Skeleton width={100} height={14} variant="rounded" />
              <Skeleton width={60} height={14} variant="rounded" />
            </div>
            {/* Row skeletons */}
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="flex gap-4 items-center py-2">
                <Skeleton width={180} height={16} variant="rounded" />
                <Skeleton width={100} height={16} variant="rounded" />
                <Skeleton width={120} height={16} variant="rounded" />
                <Skeleton width={60} height={24} variant="rounded" />
                <Skeleton width={80} height={28} variant="rounded" className="ml-auto" />
              </div>
            ))}
          </div>
        ) : (
          <>
            <FeedList feeds={feeds} onDelete={deleteFeed} />
            
            {totalPages > 1 && (
              <div className="border-t theme-border px-4 py-3 flex items-center justify-between">
                <p className="text-sm theme-text-tertiary">
                  Page {currentPage} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 theme-bg-tertiary hover:theme-bg-quaternary theme-text-primary rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1.5 theme-bg-tertiary hover:theme-bg-quaternary theme-text-primary rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </motion.div>
    </div>
  );
};

export default FormFeedsPage;
